import { useEffect } from "react";
import { toast } from "sonner";

import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import {
  dashboardExportWebSocket,
  ExportCompletedPayload,
  ExportFailedPayload,
} from "@/services/dashboard-export-websocket.service";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQueryClient } from "@tanstack/react-query";

import { useDashboardExportDownload } from "./use-dashboard-export-download";

export function useDashboardExportSocket(): void {
  const { t } = useDomainTranslation("public-dashboard");
  const queryClient = useQueryClient();
  const { download } = useDashboardExportDownload();

  useEffect(() => {
    const onCompleted = (payload: ExportCompletedPayload) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboardExports.all });
      toast.success(t("export.exportReadyTitle"), {
        description: t("export.exportReadyDescription"),
        action: {
          label: t("export.downloadAction"),
          onClick: () => {
            void download(payload);
          },
        },
      });
    };

    const onFailed = (payload: ExportFailedPayload) => {
      console.error("Dashboard export failed", payload.error);
      toast.error(t("export.exportFailedTitle"), { description: t("export.exportFailedDescription") });
    };

    dashboardExportWebSocket.connect({ onCompleted, onFailed });
    return () => {
      dashboardExportWebSocket.disconnect();
    };
  }, [t, queryClient, download]);
}
